// ============================================================================
// BOQ — SD area-rate selector / library
// ============================================================================
// Browse the cross-project corpus of Schematic-Design area-rates and, for each
// subtrade + measure-basis, see a cost-index-escalated median (with quartile
// spread and the contributing projects) to prime a new SD estimate. Composes
// useBoqSdRates (corpus) + useBoqCostIndex (escalation) + utils/sdRateStats.
// ============================================================================

import React, { useMemo, useState } from 'react';
import { Plus, Trash2, ChevronRight, ChevronDown, Tag } from 'lucide-react';
import {
  BrioCard, BrioButton, BrioInput, BrioSelect, BrioEmptyState,
  BrioInfoBanner, BrioDatePicker,
} from '../../components/BrioComponents';
import { useBoqSdRates } from '../hooks/useBoqSdRates';
import { useBoqCostIndex } from '../hooks/useBoqCostIndex';
import { useBoqTaxonomies } from '../hooks/useBoqTaxonomies';
import { useBoqBuildingTypologies } from '../hooks/useBoqBuildingTypologies';
import {
  summarizeRates, groupBySubtradeBasis, measureBasisLabel, MEASURE_BASIS_LABELS, spreadDisplay,
} from '../utils/sdRateStats';
import AddSdRateModal from '../components/AddSdRateModal';
import ManageTypologiesModal from '../components/ManageTypologiesModal';
import AreaRatesView from '../components/AreaRatesView';
import { StickyScrollBox } from '../components/StickyScrollbar';

const today = () => new Date().toISOString().slice(0, 10);
const fmt = (v) => (v == null ? '—' : `₹${Math.round(Number(v)).toLocaleString('en-IN')}`);

const cell = { padding: '0.5rem 0.65rem', fontSize: '0.85rem', borderBottom: '1px solid var(--brio-border-subtle, #eee)' };
const th = { ...cell, textAlign: 'left', fontWeight: 600, color: 'var(--brio-text-muted)', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.03em', whiteSpace: 'nowrap' };
const numCell = { ...cell, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

export default function SdRatesPage() {
  const sdApi = useBoqSdRates();
  const costIndex = useBoqCostIndex();
  const { taxonomies } = useBoqTaxonomies();
  const typoApi = useBoqBuildingTypologies();

  const [view, setView] = useState('subtrade'); // 'subtrade' | 'area'
  const [asOf, setAsOf] = useState(today());
  const [basisFilter, setBasisFilter] = useState('all');
  const [typologyFilter, setTypologyFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [showTypologies, setShowTypologies] = useState(false);

  // subtrade node id -> { label, tradeLabel, tradeId } from the brio_trade
  // taxonomy. tradeId/tradeSort anchor each subtrade to its parent chapter so
  // the table can be organised by the canonical summary-chapter principle.
  const nodeMeta = useMemo(() => {
    const map = new Map();
    const brio = taxonomies.find((t) => t.code === 'brio_trade');
    if (!brio) return map;
    const byId = new Map(brio.nodes.map((n) => [n.id, n]));
    brio.nodes.forEach((n) => {
      if (n.parentId) {
        const parent = byId.get(n.parentId);
        map.set(n.id, {
          label: n.label,
          tradeLabel: parent?.label || '',
          tradeId: n.parentId,
          tradeSort: parent?.sortOrder ?? Number.MAX_SAFE_INTEGER,
        });
      }
    });
    return map;
  }, [taxonomies]);

  // Canonical trade chapters — the brio_trade top-level nodes in taxonomy sort
  // order, numbered 1..N. Same scaffolding the project Summary tab uses, so the
  // corpus reads as a numbered chapter catalogue and missing chapters are
  // visible rather than silently absent.
  const chapters = useMemo(() => {
    const brio = taxonomies.find((t) => t.code === 'brio_trade');
    if (!brio) return [];
    return brio.nodes
      .filter((n) => !n.parentId)
      .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
      .map((n, i) => ({ id: n.id, label: n.label, num: i + 1 }));
  }, [taxonomies]);

  // Render-ordered list following the summary-chapter principle: each canonical
  // trade chapter is a header row (numbered, in taxonomy order) with its
  // summarised (subtrade × measure-basis) rows nested beneath. Empty canonical
  // chapters are still shown in the unfiltered browse so gaps are visible;
  // they're hidden once a search narrows the corpus. Returns a flat list of
  // { kind: 'chapter' | 'sub', ... } in display order.
  const rows = useMemo(() => {
    const filtered = sdApi.rates.filter((r) => (
      (basisFilter === 'all' || r.measureBasis === basisFilter)
      && (typologyFilter === 'all' || r.typologyId === typologyFilter)
    ));
    const groups = groupBySubtradeBasis(filtered);
    const subRows = [];
    groups.forEach((rates, key) => {
      const [nodeId, basis] = key.split('::');
      const meta = nodeMeta.get(nodeId) || { label: '(unmapped subtrade)', tradeLabel: '', tradeId: null };
      const stats = summarizeRates(rates, {
        adjust: costIndex.adjust, factor: costIndex.factor, asOfDate: asOf,
      });
      subRows.push({ kind: 'sub', key, nodeId, basis, ...meta, ...stats });
    });

    const q = search.trim().toLowerCase();
    const visible = subRows.filter(
      (r) => !q || r.label.toLowerCase().includes(q) || r.tradeLabel.toLowerCase().includes(q),
    );

    // Bucket subtrade rows under their parent chapter id.
    const byChapter = new Map();
    visible.forEach((r) => {
      const cid = r.tradeId || '__unmapped__';
      if (!byChapter.has(cid)) byChapter.set(cid, []);
      byChapter.get(cid).push(r);
    });

    const out = [];
    const emitChapter = (cid, label, num) => {
      const subs = (byChapter.get(cid) || []).sort(
        (a, b) => a.label.localeCompare(b.label) || a.basis.localeCompare(b.basis),
      );
      // Scaffold empty canonical chapters only in the unfiltered browse.
      if (subs.length === 0 && q) return;

      const projIds = new Set();
      const bases = new Set();
      let nSum = 0;
      let medSum = 0;
      let allMedian = true;
      subs.forEach((s) => {
        nSum += s.sampleSize;
        bases.add(s.basis);
        s.contributors.forEach((c) => { if (c.projectId != null) projIds.add(c.projectId); });
        if (s.median == null) allMedian = false; else medSum += s.median;
      });
      // Σ of subtrade medians is an additive elemental chapter rate — only valid
      // within a single measure basis, so suppress it when the chapter mixes bases.
      const sumMedian = subs.length > 0 && bases.size === 1 && allMedian ? medSum : null;

      out.push({
        kind: 'chapter',
        key: `chap:${cid}`,
        num,
        label,
        n: nSum,
        projects: projIds.size,
        sumMedian,
        mixedBasis: bases.size > 1,
        isEmpty: subs.length === 0,
      });
      subs.forEach((s) => out.push(s));
    };

    const seen = new Set();
    chapters.forEach((c) => { emitChapter(c.id, c.label, c.num); seen.add(c.id); });
    // Any subtrades whose parent isn't a canonical chapter (or is unmapped)
    // fall into a trailing, un-numbered bucket.
    [...byChapter.keys()]
      .filter((cid) => !seen.has(cid))
      .sort((a, b) => (byChapter.get(a)[0]?.tradeLabel || '').localeCompare(byChapter.get(b)[0]?.tradeLabel || ''))
      .forEach((cid) => emitChapter(cid, byChapter.get(cid)[0]?.tradeLabel || '(unmapped trade)', null));

    return out;
  }, [sdApi.rates, basisFilter, typologyFilter, nodeMeta, chapters, costIndex.adjust, costIndex.factor, asOf, search]);

  const onDelete = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Delete this SD rate from the corpus?')) return;
    const res = await sdApi.deleteMany([id]);
    // eslint-disable-next-line no-alert
    if (!res.success) window.alert(res.error || 'Delete failed.');
  };

  if (sdApi.loading) {
    return <BrioCard><div style={{ padding: '1.5rem', color: 'var(--brio-text-muted)' }}>Loading SD rates…</div></BrioCard>;
  }
  if (sdApi.error) {
    return <BrioCard><div style={{ padding: '1.5rem', color: 'var(--brio-danger, #b00)' }}>{sdApi.error}</div></BrioCard>;
  }

  const tabBtn = (active) => ({
    padding: '0.45rem 0.9rem', fontSize: '0.82rem', fontWeight: active ? 700 : 500,
    border: '1px solid var(--brio-border, #ddd)', cursor: 'pointer',
    background: active ? 'var(--brio-gold, #9F902A)' : 'transparent',
    color: active ? '#fff' : 'var(--brio-text-muted)',
  });

  return (
    <div style={{ fontFamily: 'Raleway, sans-serif' }}>
      {/* View toggle + shared pricing date */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', borderRadius: 6, overflow: 'hidden' }}>
          <button type="button" style={{ ...tabBtn(view === 'subtrade'), borderRadius: '6px 0 0 6px' }} onClick={() => setView('subtrade')}>
            Subtrade rates
          </button>
          <button type="button" style={{ ...tabBtn(view === 'area'), borderRadius: '0 6px 6px 0', borderLeft: 'none' }} onClick={() => setView('area')}>
            Area rates (all-in)
          </button>
        </div>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>
          Price as of
          <BrioDatePicker value={asOf} onChange={(v) => setAsOf(v || today())} clearable={false} />
        </label>
        <BrioButton variant="secondary" onClick={() => setShowTypologies(true)}>
          <Tag size={15} style={{ marginRight: 4, verticalAlign: '-2px' }} />Typologies
        </BrioButton>
      </div>

      {view === 'area' ? (
        <AreaRatesView asOf={asOf} costIndex={costIndex} typologies={typoApi.typologies} />
      ) : (
      <>
      {/* Subtrade toolbar */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>
          Measure basis
          <BrioSelect value={basisFilter} onChange={(e) => setBasisFilter(e.target.value)}>
            <option value="all">All bases</option>
            {Object.entries(MEASURE_BASIS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </BrioSelect>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.78rem', color: 'var(--brio-text-muted)' }}>
          Typology
          <BrioSelect value={typologyFilter} onChange={(e) => setTypologyFilter(e.target.value)}>
            <option value="all">All typologies</option>
            {typoApi.typologies.filter((t) => t.isActive).map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </BrioSelect>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.78rem', color: 'var(--brio-text-muted)', flex: '1 1 220px' }}>
          Search subtrade / trade
          <BrioInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="e.g. flooring, electrical…" />
        </label>
        <BrioButton onClick={() => setShowAdd(true)}>
          <Plus size={15} style={{ marginRight: 4, verticalAlign: '-2px' }} />Add rate
        </BrioButton>
      </div>

      <BrioInfoBanner>
        Organised by the canonical trade chapters (1–{chapters.length || '…'}), in summary order — each
        chapter heads its subtrades, and the chapter Σ is the additive total of its subtrade medians
        (shown only within a single measure basis). Medians are escalated to <strong>{asOf}</strong> via
        the construction cost index (region IN). Rates of different measure bases are not comparable, so
        each subtrade × basis is summarised separately.
        {!costIndex.hasData && ' ⚠ No cost-index data loaded — showing un-escalated rates.'}
      </BrioInfoBanner>

      {!rows.some((r) => r.kind === 'sub') ? (
        <BrioCard>
          <BrioEmptyState>
            <div style={{ marginBottom: '0.75rem' }}>
              No SD area-rates yet for this filter. The corpus grows as SD estimates are priced per-sqm.
            </div>
            <BrioButton onClick={() => setShowAdd(true)}>Add the first rate</BrioButton>
          </BrioEmptyState>
        </BrioCard>
      ) : (
        <BrioCard>
          <StickyScrollBox style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 28 }} />
                  <th style={th}>Subtrade</th>
                  <th style={th}>Basis</th>
                  <th style={{ ...th, textAlign: 'right' }}>n</th>
                  <th style={{ ...th, textAlign: 'right' }}>Projects</th>
                  <th style={{ ...th, textAlign: 'right' }}>Median /sqm</th>
                  <th style={{ ...th, textAlign: 'right' }}>p25–p75</th>
                  <th style={{ ...th, textAlign: 'right' }}>Range</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  // Chapter header row — numbered, with its additive Σ median.
                  if (r.kind === 'chapter') {
                    return (
                      <tr key={r.key} style={{ background: 'var(--brio-gold-faint, #f7f1d4)', color: r.isEmpty ? 'var(--brio-text-muted)' : undefined }}>
                        <td style={{ ...cell }} />
                        <td colSpan={2} style={{ ...cell, fontWeight: 700 }}>
                          {r.num ? `${r.num}. ` : ''}{r.label}{r.isEmpty ? ' — (no rates yet)' : ''}
                        </td>
                        <td style={numCell}>{r.isEmpty ? '' : r.n}</td>
                        <td style={numCell}>{r.isEmpty ? '' : r.projects}</td>
                        <td style={{ ...numCell, fontWeight: 700, color: 'var(--brio-gold, #9F902A)' }}>
                          {r.sumMedian != null ? fmt(r.sumMedian) : (r.mixedBasis ? <span style={{ fontWeight: 400, color: 'var(--brio-text-muted)', fontSize: '0.74rem' }}>mixed basis</span> : '')}
                        </td>
                        <td style={numCell} />
                        <td style={numCell} />
                      </tr>
                    );
                  }
                  const isOpen = expanded === r.key;
                  const spread = spreadDisplay(r);
                  return (
                    <React.Fragment key={r.key}>
                      <tr
                        onClick={() => setExpanded(isOpen ? null : r.key)}
                        style={{ cursor: 'pointer', background: isOpen ? 'var(--brio-bg, #f7f7f5)' : 'transparent' }}
                      >
                        <td style={{ ...cell, textAlign: 'center', color: 'var(--brio-text-muted)' }}>
                          {isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                        </td>
                        <td style={{ ...cell, fontWeight: 600, paddingLeft: '1.5rem' }}>{r.label}</td>
                        <td style={cell}>{measureBasisLabel(r.basis)}</td>
                        <td style={numCell}>{r.sampleSize}</td>
                        <td style={numCell}>{r.distinctProjects}</td>
                        <td style={{ ...numCell, fontWeight: 700, color: 'var(--brio-gold, #9F902A)' }}>{fmt(r.median)}</td>
                        <td style={{ ...numCell, color: 'var(--brio-text-muted)' }}>
                          {spread.quartiles ? `${fmt(spread.quartiles[0])}–${fmt(spread.quartiles[1])}` : <span style={{ fontStyle: 'italic', fontSize: '0.74rem' }}>{spread.note}</span>}
                        </td>
                        <td style={{ ...numCell, color: 'var(--brio-text-muted)' }}>
                          {spread.range ? `${fmt(spread.range[0])}–${fmt(spread.range[1])}` : '—'}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={8} style={{ padding: 0, background: 'var(--brio-bg, #f7f7f5)' }}>
                            <div style={{ padding: '0.5rem 1rem 0.9rem 2.5rem' }}>
                              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                  <tr>
                                    <th style={th}>Project</th>
                                    <th style={th}>Source</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Original /sqm</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Rate date</th>
                                    <th style={{ ...th, textAlign: 'right' }}>Escalated /sqm</th>
                                    <th style={{ ...th, width: 36 }} />
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.contributors.map((c) => (
                                    <tr key={c.id}>
                                      <td style={cell}>
                                        {c.projectCode ? `${c.projectCode} — ${c.projectName || ''}` : '(no project)'}
                                        {(c.zoneLabel || c.typologyLabel) && (
                                          <span style={{ color: 'var(--brio-text-muted)', fontSize: '0.72rem', marginLeft: 6 }}>
                                            {[c.zoneLabel, c.typologyLabel].filter(Boolean).join(' · ')}
                                          </span>
                                        )}
                                      </td>
                                      <td style={{ ...cell, color: 'var(--brio-text-muted)' }}>{c.source}</td>
                                      <td style={numCell}>{fmt(c.rate)}</td>
                                      <td style={{ ...numCell, color: 'var(--brio-text-muted)' }}>{c.rateDate || '—'}</td>
                                      <td style={numCell}>
                                        {fmt(c.escalatedRate)}
                                        {c.escalationFactor != null && c.escalationFactor !== 1 && (
                                          <span style={{ color: 'var(--brio-text-muted)', fontSize: '0.72rem' }}> ×{c.escalationFactor.toFixed(3)}</span>
                                        )}
                                      </td>
                                      <td style={{ ...cell, textAlign: 'center' }}>
                                        <button
                                          type="button"
                                          title="Delete rate"
                                          onClick={(e) => { e.stopPropagation(); onDelete(c.id); }}
                                          style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--brio-text-muted)' }}
                                        >
                                          <Trash2 size={14} />
                                        </button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </StickyScrollBox>
        </BrioCard>
      )}
      </>
      )}

      {showAdd && (
        <AddSdRateModal
          taxonomies={taxonomies}
          typologies={typoApi.typologies}
          onClose={() => setShowAdd(false)}
          onCreate={async (rows2) => {
            const res = await sdApi.createRates(rows2);
            if (res.success) setShowAdd(false);
            return res;
          }}
        />
      )}

      {showTypologies && (
        <ManageTypologiesModal api={typoApi} onClose={() => setShowTypologies(false)} />
      )}
    </div>
  );
}
